import { ActionIcon, Button, Group, Menu, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDots, IconEye, IconTrash, IconUpload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { TravelReferenceDocumentSlot } from '../model/TravelReferenceDocumentTypes';

interface TravelReferenceDocumentSlotActionsProps {
  slot: TravelReferenceDocumentSlot;
  label: string;
  onView: () => void;
  onUpload: () => void;
  onDelete: () => Promise<unknown>;
  isDeleting: boolean;
}

export function TravelReferenceDocumentSlotActions(props: TravelReferenceDocumentSlotActionsProps) {
  const { slot, label, onView, onUpload, onDelete, isDeleting } = props;
  const { t } = useTranslation();
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  if (!slot.present) {
    return (
      <Button
        size="xs"
        variant="outline"
        flex="none"
        leftSection={<IconUpload size={14} />}
        aria-label={`${t('travel.referenceDocuments.upload')}: ${label}`}
        onClick={onUpload}
      >
        {t('travel.referenceDocuments.upload')}
      </Button>
    );
  }

  return (
    <>
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={`${t('travel.referenceDocuments.actions.menu')}: ${label}`}
          >
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconEye size={14} />}
            onClick={onView}
          >
            {t('button.view')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconUpload size={14} />}
            onClick={onUpload}
          >
            {t('travel.referenceDocuments.replace')}
          </Menu.Item>
          <Menu.Item
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={openConfirm}
          >
            {t('button.delete')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title={t('travel.referenceDocuments.actions.confirmDelete.title')}
        centered
      >
        <Stack>
          <Text
            style={{ textAlign: 'center' }}
            size="sm"
          >
            {label}
          </Text>
          <Text
            style={{ textAlign: 'center' }}
            fw="500"
          >
            {t('travel.referenceDocuments.actions.confirmDelete.content')}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={closeConfirm}
              loading={isDeleting}
            >
              {t('button.cancel')}
            </Button>
            <Button
              onClick={() => onDelete().then(closeConfirm)}
              bg="red"
              loading={isDeleting}
              leftSection={<IconTrash size={16} />}
            >
              {t('button.delete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
